/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useServerSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling form submission, validation, and API calls.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createTelephonyServer, updateTelephonyServer } from '../api';
import { TelephonyServerFormData, TelephonyServer } from '../types';

export function useServerSubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: TelephonyServerFormData,
  originalData: TelephonyServerFormData,
  hasChanges: boolean,
  setOriginalData: (data: TelephonyServerFormData) => void,
) {
  const { user } = useAuthStore();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug || '';

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('telephonyServers.validation.nameRequired');
    }

    if (!formData.host.trim()) {
      newErrors.host = t('telephonyServers.validation.hostRequired');
    } else if (!/^[a-zA-Z0-9.-]+$/.test(formData.host)) {
      newErrors.host = t('telephonyServers.validation.invalidHost');
    }

    if (!formData.username.trim()) {
      newErrors.username = t('telephonyServers.validation.usernameRequired');
    }

    if (formData.port < 1 || formData.port > 65535) {
      newErrors.port = t('telephonyServers.validation.portRange');
    }

    if (!formData.eslHost?.trim()) {
      if (formData.host?.trim()) {
        formData.eslHost = formData.host;
      } else {
        newErrors.eslHost = t('telephonyServers.validation.eslHostRequired');
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [formData, t]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<TelephonyServer | undefined> => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Check if there are changes
      // hasChanges validation removed

      const validation = validateForm();
      if (!validation.isValid) {
        const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' • ');
        showError(`${t('telephonyServers.messages.validationError', 'Please fix validation errors')}${errorMsgs ? ': ' + errorMsgs : ''}`);
        return undefined;
      }

      try {
        setIsLoading(true);

        const serverData =
          mode === 'create'
            ? await createTelephonyServer(formData, tenantSlug, user?.id)
            : await updateTelephonyServer(id!, formData, tenantSlug, user?.id);

        showSuccess(
          mode === 'create'
            ? t('telephonyServers.messages.createSuccess')
            : t('telephonyServers.messages.updateSuccess'),
        );

        // Update originalData after save
        const savedData: TelephonyServerFormData = {
          ...formData,
        };
        (savedData as any).id = serverData.id;
        setOriginalData(savedData);

        return serverData;
      } catch (error) {
        let errorMsg = t('telephonyServers.failedToSaveServer');
        if (error instanceof Error) {
          if (error.message.includes('VALIDATION_ERROR')) {
            errorMsg = t('telephonyServers.validationError');
          } else if (error.message.includes('400')) {
            errorMsg = t('telephonyServers.invalidData');
          } else if (error.message.includes('409')) {
            errorMsg = t('telephonyServers.nameExists');
          } else {
            errorMsg = error.message;
          }
        }
        showError(errorMsg);
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [
      mode,
      id,
      formData,
      hasChanges,
      validateForm,
      tenantSlug,
      user?.id,
      setOriginalData,
      showSuccess,
      showError,
      showInfo,
      t,
    ],
  );

  return {
    isLoading,
    errors,
    setErrors,
    handleSubmit,
  };
}

